import React, { useState, useEffect, useRef } from "react";
import { Button } from "flowbite-react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";

function ResetPasswordModal({ user, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSave = async () => {
    if (!password) {
      toast.warning("Password cannot be empty.");
      return;
    }
    if (password !== confirm) {
      toast.warning("Passwords do not match.");
      return;
    }
    try {
      await axios.patch(
        `https://eps.elkomprintama.com/patch/api/user/admin-reset/${user.iduser}`,
        { password },
      );
      toast.success(`Password reset for ${user.username}!`);
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to reset password.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-80 p-6">
        <h3 className="text-sm font-bold uppercase text-gray-700 mb-1">
          Reset Password
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          User: <span className="font-semibold">{user.username}</span>
        </p>

        <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
          New Password
        </label>
        <input
          ref={inputRef}
          type="password"
          className="w-full border border-green-400 rounded py-2 px-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-green-400"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") onClose();
          }}
        />

        <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
          Confirm Password
        </label>
        <input
          type="password"
          className="w-full border border-green-400 rounded py-2 px-3 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-green-400"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") onClose();
          }}
        />

        <div className="flex gap-3">
          <Button color="success" fullSized onClick={handleSave}>
            Save
          </Button>
          <Button color="gray" fullSized onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function UsersComponent() {
  const [userLevel, setUserLevel] = useState([]);
  const [userList, setUserList] = useState([]);
  const [userName, setUserName] = useState("");
  const [userPass, setUserPass] = useState("");
  const [confUserPass, setConfUserPass] = useState("");
  const [selectedUserLevel, setSelectedUserLevel] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    getUserLevel();
    getAllUsers();
  }, []);

  const getUserLevel = async () => {
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/users/levels",
      );
      setUserLevel(res.data || []);
    } catch {
      toast.error("Failed to fetch user levels.");
    }
  };

  const getAllUsers = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/users",
      );
      setUserList(res.data || []);
    } catch {
      toast.error("Failed to fetch users.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userName || !userPass) {
      toast.warning("Please fill in all fields.");
      return;
    }
    if (userPass !== confUserPass) {
      toast.warning("Passwords do not match.");
      return;
    }
    if (selectedUserLevel === 0) {
      toast.warning("Please select a level.");
      return;
    }
    setShowConfirm(true);
  };

  const newUser = async () => {
    try {
      setIsLoading(true);
      await axios.post("https://eps.elkomprintama.com/post/api/users", {
        username: userName,
        password: userPass,
        confPassword: confUserPass,
        user_lvl: selectedUserLevel,
      });
      toast.success("User added successfully!");
      setUserName("");
      setUserPass("");
      setConfUserPass("");
      setSelectedUserLevel(0);
      getAllUsers();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to add user.");
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserLevel = async (userId, newLevel) => {
    try {
      await axios.patch(
        `https://eps.elkomprintama.com/patch/api/user/level/${userId}`,
        {
          user_lvl: newLevel,
        },
      );
      toast.success("User level updated!");
      getAllUsers();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to update level.");
      getAllUsers(); // revert display on failure
    }
  };

  const deleteUser = async (userId) => {
    try {
      setIsLoading(true);
      await axios.delete(
        `https://eps.elkomprintama.com/delete/api/user/${userId}`,
      );
      toast.success("User deleted!");
      getAllUsers();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to delete user.");
    } finally {
      setIsLoading(false);
      setDeleteTarget(null);
    }
  };

  function applyStripedStyle(index) {
    return index % 2 === 0 ? "bg-gray-50" : "bg-white";
  }

  return (
    <>
      <div className="w-full rounded-lg bg-blue-700">
        <label className="block uppercase text-white mb-2 tracking-wide text-5xl text-center font-bold">
          Users
        </label>
      </div>

      {/* Add User Form */}
      <div className="bg-gray-100 shadow-md px-5 py-5 rounded-lg mt-2 mb-6">
        <h2 className="text-sm font-bold uppercase text-gray-600 mb-4">
          Add New User
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-wrap -mx-3 mb-4">
            <div className="w-full md:w-1/2 px-3 mb-4 md:mb-0">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Username
              </label>
              <input
                className="appearance-none block w-full border bg-white border-green-400 rounded py-3 px-4 leading-tight focus:outline-none"
                type="text"
                placeholder="Username"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
            </div>
            <div className="w-full md:w-1/2 px-3">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Level
              </label>
              <select
                className="block w-full border border-green-400 text-gray-700 py-3 px-4 rounded leading-tight focus:outline-none"
                value={selectedUserLevel}
                onChange={(e) => setSelectedUserLevel(e.target.value)}
              >
                <option value={0}>Please Select</option>
                {userLevel.map((lvl) => (
                  <option key={lvl.user_lvl} value={lvl.user_lvl}>
                    {lvl.level}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 mb-4">
            <div className="w-full md:w-1/2 px-3 mb-4 md:mb-0">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Password
              </label>
              <input
                className="appearance-none block w-full border bg-white border-green-400 rounded py-3 px-4 leading-tight focus:outline-none"
                type="password"
                placeholder="Password"
                value={userPass}
                onChange={(e) => setUserPass(e.target.value)}
              />
            </div>
            <div className="w-full md:w-1/2 px-3">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Confirm Password
              </label>
              <input
                className="appearance-none block w-full border bg-white border-green-400 rounded py-3 px-4 leading-tight focus:outline-none"
                type="password"
                placeholder="Confirm Password"
                value={confUserPass}
                onChange={(e) => setConfUserPass(e.target.value)}
              />
            </div>
          </div>

          <Button fullSized type="submit">
            Add User
          </Button>
        </form>
      </div>

      {/* User List */}
      <div className="bg-gray-100 shadow-md px-5 py-5 rounded-lg relative">
        <h2 className="text-sm font-bold uppercase text-gray-600 mb-4">
          All Users
        </h2>
        {isLoading && <LoadingOverlay message="Loading..." />}

        <div className="overflow-x-auto rounded-lg shadow">
          <table className="text-sm text-left w-full border-collapse">
            <thead className="text-xs text-white uppercase bg-gray-700">
              <tr>
                <th className="px-5 py-3 border-r">No</th>
                <th className="px-5 py-3 border-r">Username</th>
                <th className="px-5 py-3 border-r">Level</th>
                <th className="px-5 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {userList.map((u, index) => (
                <tr
                  key={u.iduser}
                  className={`text-xs border-b hover:bg-blue-50 ${applyStripedStyle(index)}`}
                >
                  <td className="px-5 py-3 border-r">{index + 1}</td>
                  <td className="px-5 py-3 border-r font-semibold">
                    {u.username}
                  </td>
                  <td className="px-5 py-3 border-r">
                    <select
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-blue-400"
                      value={u.user_lvl}
                      onChange={(e) =>
                        updateUserLevel(u.iduser, e.target.value)
                      }
                    >
                      {userLevel.map((lvl) => (
                        <option key={lvl.user_lvl} value={lvl.user_lvl}>
                          [{lvl.user_lvl}] {lvl.level}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        className="text-xs text-yellow-600 hover:underline font-medium"
                        onClick={() => setResetTarget(u)}
                      >
                        Reset Password
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        className="text-xs text-red-500 hover:underline font-medium"
                        onClick={() => setDeleteTarget(u)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add user confirm */}
      {showConfirm && (
        <ConfirmDialog
          message="Are you sure you want to add this user?"
          onConfirm={() => {
            setShowConfirm(false);
            newUser();
          }}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Delete user confirm */}
      {deleteTarget && (
        <ConfirmDialog
          message={`Delete user "${deleteTarget.username}"? This cannot be undone.`}
          onConfirm={() => deleteUser(deleteTarget.iduser)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onSaved={getAllUsers}
        />
      )}
    </>
  );
}

export default UsersComponent;
